import React, { useState, useEffect } from 'react';
import { useDataContext } from '../../context/DataContext';
import { MenuItem, MenuCategory } from '../../types';

// Custom Confirmation Dialog Component
interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

// Custom Notification Component
interface NotificationProps {
  isOpen: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  onClose: () => void;
  duration?: number;
}

const Notification: React.FC<NotificationProps> = ({
  isOpen,
  message,
  type,
  onClose,
  duration = 5000
}) => {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: '#34c759',
          borderColor: '#30d158',
          icon: '✅'
        };
      case 'error':
        return {
          backgroundColor: '#ff3b30',
          borderColor: '#ff453a',
          icon: '❌'
        };
      case 'warning':
        return {
          backgroundColor: '#ff9500',
          borderColor: '#ff9f0a',
          icon: '⚠️'
        };
      default:
        return {
          backgroundColor: '#0071e3',
          borderColor: '#0a84ff',
          icon: 'ℹ️'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      backgroundColor: styles.backgroundColor,
      color: 'white',
      padding: '16px 20px',
      borderRadius: '12px',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: `1px solid ${styles.borderColor}`,
      zIndex: 1001,
      maxWidth: '400px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      animation: 'slideInRight 0.3s ease-out'
    }}>
      <span style={{ fontSize: '18px' }}>{styles.icon}</span>
      <span style={{ 
        fontSize: '0.875rem', 
        fontWeight: '500',
        flex: 1
      }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: 'white',
          fontSize: '18px',
          cursor: 'pointer',
          padding: '0',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ×
      </button>
    </div>
  );
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'danger'
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          borderColor: '#ff3b30',
          confirmBg: '#ff3b30',
          confirmHover: '#d70015'
        };
      case 'warning':
        return {
          borderColor: '#ff9500',
          confirmBg: '#ff9500',
          confirmHover: '#e6850e'
        };
      default:
        return {
          borderColor: '#0071e3',
          confirmBg: '#0071e3',
          confirmHover: '#005bbf'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        border: `2px solid ${styles.borderColor}`,
        animation: 'slideIn 0.2s ease-out'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: `${styles.borderColor}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            {type === 'danger' && (
              <span style={{ fontSize: '20px', color: styles.borderColor }}>⚠️</span>
            )}
            {type === 'warning' && (
              <span style={{ fontSize: '20px', color: styles.borderColor }}>⚠️</span>
            )}
            {type === 'info' && (
              <span style={{ fontSize: '20px', color: styles.borderColor }}>ℹ️</span>
            )}
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#1d1d1f'
          }}>
            {title}
          </h3>
        </div>
        
        <p style={{
          margin: '0 0 24px 0',
          fontSize: '0.875rem',
          lineHeight: '1.5',
          color: '#86868b'
        }}>
          {message}
        </p>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: styles.confirmBg,
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = styles.confirmHover;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = styles.confirmBg;
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom Modal Component for Mobile
interface ModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, title, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }} className="menu-edit-modal">
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }} className="menu-edit-modal-content">
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: '#86868b'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '20px' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const MenuManagement: React.FC = () => {
  const {
    menuItems,
    loadingMenu,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    menuCategories,
    loadingCategories,
    createMenuCategory,
    updateMenuCategory,
    deleteMenuCategory,
  } = useDataContext();

  const [activeTab, setActiveTab] = useState<'items' | 'categories'>('items');
  const [isMobile, setIsMobile] = useState(false);
  
  // Menu item state
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    available: true,
    imageUrl: '',
  });
  const [imagePreview, setImagePreview] = useState<string>('');

  // Category state
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    displayOrder: 0,
    isActive: true,
  });

  // Confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    type: 'item' | 'category';
    item?: MenuItem;
    category?: MenuCategory;
  }>({
    isOpen: false,
    type: 'item'
  });

  // Notification state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    message: '',
    type: 'info'
  });

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset forms when switching tabs
  useEffect(() => {
    resetForm();
    resetCategoryForm();
  }, [activeTab]);

  // Use actual categories from database, with fallback to default categories
  const getCategoryOptions = () => {
    if (menuCategories.length > 0) {
      return menuCategories
        .filter(cat => cat.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(cat => ({
          value: cat.id.toString(),
          label: cat.name
        }));
    }
    
    // Fallback to default categories if none exist
    return [
      { value: '1', label: 'Coffee' },
      { value: '2', label: 'Tea' },
      { value: '3', label: 'Food' },
      { value: '4', label: 'Desserts' },
      { value: '5', label: 'Cold Drinks' },
    ];
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      available: true,
      imageUrl: '',
    });
    setImagePreview('');
    setEditingItem(null);
    setIsCreating(false);
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      displayOrder: 0,
      isActive: true,
    });
    setEditingCategory(null);
    setIsCreatingCategory(false);
  };

  const handleEdit = (item: MenuItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      category: item.categoryId?.toString() || '',
      available: item.isAvailable,
      imageUrl: item.imageUrl || '',
    });
    setImagePreview(item.imageUrl || '');
    setIsCreating(false);
    // Switch to items tab if not already there
    if (activeTab !== 'items') {
      setActiveTab('items');
    }
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
    // Switch to items tab if not already there
    if (activeTab !== 'items') {
      setActiveTab('items');
    }
  };

  const handleEditCategory = (category: MenuCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      displayOrder: category.displayOrder,
      isActive: category.isActive,
    });
    setIsCreatingCategory(false);
    // Switch to categories tab if not already there
    if (activeTab !== 'categories') {
      setActiveTab('categories');
    }
  };

  const handleCreateCategory = () => {
    resetCategoryForm();
    setIsCreatingCategory(true);
    // Switch to categories tab if not already there
    if (activeTab !== 'categories') {
      setActiveTab('categories');
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (limit to 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showNotification('Image size should be less than 2MB', 'warning');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification('Please select a valid image file', 'warning');
        return;
      }

      try {
        const base64 = await convertFileToBase64(file);
        setImagePreview(base64);
        setFormData({ ...formData, imageUrl: base64 });
        showNotification('Image uploaded successfully', 'success');
      } catch (error) {
        console.error('Error processing image file:', error);
        showNotification('Error processing image file', 'error');
      }
    }
  };

  const handleImageUrlChange = (url: string) => {
    setFormData({ ...formData, imageUrl: url });
    setImagePreview(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.price.trim()) {
      showNotification('Name and price are required', 'warning');
      return;
    }

    const price = parseFloat(formData.price);
    if (isNaN(price) || price <= 0) {
      showNotification('Please enter a valid price', 'warning');
      return;
    }

    try {
      const itemData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price,
        categoryId: formData.category && formData.category !== '' ? parseInt(formData.category) : undefined,
        isAvailable: formData.available,
        isFeatured: false,
        displayOrder: 0,
        currency: 'USD',
        imageUrl: formData.imageUrl.trim() || undefined,
      };

      if (editingItem) {
        await updateMenuItem(editingItem.id, itemData);
        showNotification(`${editingItem.name} updated successfully`, 'success');
      } else {
        await createMenuItem(itemData);
        showNotification('Menu item created successfully', 'success');
      }

      resetForm();
    } catch (error) {
      console.error('Failed to save menu item:', error);
      showNotification('Failed to save menu item. Please try again.', 'error');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const item = menuItems.find(item => item.id === id);
    if (item) {
      setDeleteDialog({
        isOpen: true,
        type: 'item',
        item
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      if (deleteDialog.type === 'item' && deleteDialog.item) {
        await deleteMenuItem(deleteDialog.item.id);
        showNotification(`${deleteDialog.item.name} deleted successfully`, 'success');
      } else if (deleteDialog.type === 'category' && deleteDialog.category) {
        await deleteMenuCategory(deleteDialog.category.id);
        showNotification(`Category "${deleteDialog.category.name}" and all its items deleted successfully`, 'success');
      }
      setDeleteDialog({ isOpen: false, type: 'item' });
    } catch (error) {
      console.error('Failed to delete:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete item. Please try again.';
      showNotification(errorMessage, 'error');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, type: 'item' });
  };

  // Helper functions for notifications
  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({
      isOpen: true,
      message,
      type
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryFormData.name.trim()) {
      showNotification('Category name is required', 'warning');
      return;
    }

    try {
      if (editingCategory) {
        const categoryData = {
          name: categoryFormData.name.trim(),
          description: categoryFormData.description.trim() || undefined,
          displayOrder: categoryFormData.displayOrder,
          isActive: categoryFormData.isActive,
        };
        await updateMenuCategory(editingCategory.id, categoryData);
        showNotification(`Category "${editingCategory.name}" updated successfully`, 'success');
      } else {
        const categoryData = {
          name: categoryFormData.name.trim(),
          description: categoryFormData.description.trim() || undefined,
          displayOrder: categoryFormData.displayOrder,
          isActive: categoryFormData.isActive,
        };
        await createMenuCategory(categoryData);
        showNotification('Category created successfully', 'success');
      }

      resetCategoryForm();
    } catch (error) {
      console.error('Failed to save category:', error);
      showNotification('Failed to save category. Please try again.', 'error');
    }
  };

  const handleDeleteCategory = async (id: number, name: string) => {
    const category = menuCategories.find(cat => cat.id === id);
    if (category) {
      setDeleteDialog({
        isOpen: true,
        type: 'category',
        category
      });
    }
  };

  const menuByCategory = menuItems.reduce((acc, item) => {
    const categoryKey = item.categoryId?.toString() || 'uncategorized';
    if (!acc[categoryKey]) {
      acc[categoryKey] = [];
    }
    acc[categoryKey].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  // Add uncategorized items to the display
  const allCategoryOptions = [
    ...getCategoryOptions(),
    ...(menuByCategory['uncategorized']?.length > 0 ? [{ value: 'uncategorized', label: 'No Category' }] : [])
  ];

  // Get dialog content based on type
  const getDialogContent = () => {
    if (deleteDialog.type === 'item' && deleteDialog.item) {
      return {
        title: 'Delete Menu Item',
        message: `Are you sure you want to delete "${deleteDialog.item.name}"? This action cannot be undone.`,
        confirmText: 'Delete Item',
        cancelText: 'Cancel',
        type: 'danger' as const
      };
    } else if (deleteDialog.type === 'category' && deleteDialog.category) {
      const itemCount = menuItems.filter(item => item.categoryId === deleteDialog.category!.id).length;
      const itemText = itemCount === 1 ? '1 item' : `${itemCount} items`;
      return {
        title: 'Delete Category',
        message: `Are you sure you want to delete category "${deleteDialog.category.name}"? This will also delete all ${itemText} in this category. This action cannot be undone.`,
        confirmText: 'Delete Category & Items',
        cancelText: 'Cancel',
        type: 'danger' as const
      };
    }
    return {
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this item?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger' as const
    };
  };

  const dialogContent = getDialogContent();

  return (
    <div className="main-content">
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={dialogContent.title}
        message={dialogContent.message}
        confirmText={dialogContent.confirmText}
        cancelText={dialogContent.cancelText}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type={dialogContent.type}
      />

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />

      <div className="hero" style={{
        background: 'linear-gradient(135deg, rgba(0, 113, 227, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)',
        paddingTop: '60px',
        paddingBottom: '60px'
      }}>
        <h1 style={{ fontSize: '2.5rem' }}>Menu Management</h1>
        <p>Create and manage your cafe's menu items and categories</p>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          gap: '0'
        }}>
          <button
            onClick={() => setActiveTab('items')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'items' ? '#0071e3' : 'transparent',
              color: activeTab === 'items' ? 'white' : '#1d1d1f',
              fontWeight: '500',
              cursor: 'pointer',
              borderBottom: activeTab === 'items' ? '2px solid #0071e3' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}
          >
            Menu Items ({menuItems.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'categories' ? '#0071e3' : 'transparent',
              color: activeTab === 'categories' ? 'white' : '#1d1d1f',
              fontWeight: '500',
              cursor: 'pointer',
              borderBottom: activeTab === 'categories' ? '2px solid #0071e3' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}
          >
            Categories ({menuCategories.length})
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: (isCreating || editingItem || isCreatingCategory || editingCategory) ? '1fr minmax(300px, 450px)' : '1fr',
        gap: '32px',
        transition: 'grid-template-columns 0.3s ease'
      }} className="menu-management-grid">
        {/* Content Area */}
        <div>
          {activeTab === 'items' ? (
            /* Menu Items Tab */
            <div className="card">
              <div className="card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="card-title">Menu Items</h3>
                  <button
                    className="btn btn-primary"
                    onClick={handleCreate}
                  >
                    Add New Item
                  </button>
                </div>
              </div>

            {loadingMenu ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Loading menu...</p>
              </div>
            ) : (
              <div>
                {allCategoryOptions.map(({ value, label }) => {
                  const items = menuByCategory[value] || [];
                  if (items.length === 0) return null;

                  return (
                    <div key={value} className="menu-category">
                      <h4 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#1d1d1f',
                        marginBottom: '16px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                        letterSpacing: '-0.01em'
                      }}>
                        {label}
                      </h4>
                      
                      {items.map(item => (
                        <div key={item.id} className="menu-item" style={{
                          ...(editingItem?.id === item.id && {
                            border: '2px solid #0071e3',
                            borderRadius: '12px',
                            backgroundColor: 'rgba(0, 113, 227, 0.05)'
                          })
                        }}>
                          {item.imageUrl && (
                            <div style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              flexShrink: 0,
                              marginRight: '16px'
                            }}>
                              <img
                                src={item.imageUrl}
                                alt={item.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="menu-item-info">
                            <div className="menu-item-name">
                              {item.name}
                              {!item.isAvailable && (
                                <span style={{
                                  marginLeft: '8px',
                                  padding: '2px 8px',
                                  fontSize: '0.75rem',
                                  backgroundColor: 'rgba(255, 59, 48, 0.15)',
                                  color: '#d70015',
                                  borderRadius: '8px',
                                  fontWeight: '600'
                                }}>
                                  UNAVAILABLE
                                </span>
                              )}
                            </div>
                            <div className="menu-item-description">{item.description}</div>
                            <div className="menu-item-price">${item.price.toFixed(2)}</div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleEdit(item)}
                              style={{ fontSize: '0.8rem', padding: '8px 16px' }}
                            >
                              {editingItem?.id === item.id ? 'Editing...' : 'Edit'}
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDelete(item.id, item.name)}
                              style={{ fontSize: '0.8rem', padding: '8px 16px' }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {menuItems.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#86868b' }}>
                    <p>No menu items yet. Create your first item!</p>
                  </div>
                )}

                {menuItems.length > 0 && !isCreating && !editingItem && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    color: '#86868b',
                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    marginTop: '20px'
                  }}>
                    <p>👆 Click "Add New Item" to create a menu item, or click "Edit" on any item to modify it.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Categories Tab */
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title">Menu Categories</h3>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateCategory}
                >
                  Add New Category
                </button>
              </div>
            </div>

            {loadingCategories ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Loading categories...</p>
              </div>
            ) : (
              <div>
                {menuCategories.map(category => (
                  <div key={category.id} className="menu-item" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                    gap: '16px',
                    ...(editingCategory?.id === category.id && {
                      border: '2px solid #0071e3',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(0, 113, 227, 0.05)'
                    })
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#1d1d1f',
                        marginBottom: '4px'
                      }}>
                        {category.name}
                        {!category.isActive && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(255, 59, 48, 0.15)',
                            color: '#d70015',
                            borderRadius: '8px',
                            fontWeight: '600'
                          }}>
                            INACTIVE
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#86868b',
                        marginBottom: '4px'
                      }}>
                        {category.description || 'No description'}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#86868b'
                      }}>
                        Display Order: {category.displayOrder}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditCategory(category)}
                        style={{ fontSize: '0.8rem', padding: '8px 16px' }}
                      >
                        {editingCategory?.id === category.id ? 'Editing...' : 'Edit'}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDeleteCategory(category.id, category.name)}
                        style={{ fontSize: '0.8rem', padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                {menuCategories.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#86868b' }}>
                    <p>No categories yet. Create your first category!</p>
                  </div>
                )}

                {menuCategories.length > 0 && !isCreatingCategory && !editingCategory && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    color: '#86868b',
                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    marginTop: '20px'
                  }}>
                    <p>👆 Click "Add New Category" to create a category, or click "Edit" on any category to modify it.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Forms - Sidebar (Desktop) */}
      {(isCreating || editingItem || isCreatingCategory || editingCategory) && !isMobile && (
        <div style={{ 
          position: 'sticky',
          top: '24px',
          height: 'fit-content',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto'
        }} className="menu-form-sidebar">
          {/* Menu Item Form */}
          {(isCreating || editingItem) && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {editingItem ? 'Edit Menu Item' : 'Create New Menu Item'}
                </h3>
                {editingItem && (
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#0071e3',
                    fontWeight: '500',
                    marginTop: '4px'
                  }}>
                    Editing: {editingItem.name}
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="itemName">Name *</label>
                  <input
                    id="itemName"
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter item name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="itemDescription">Description</label>
                  <textarea
                    id="itemDescription"
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the item..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="itemPrice">Price *</label>
                  <input
                    id="itemPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="itemCategory">Category</label>
                  <select
                    id="itemCategory"
                    className="form-select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="">Select a category</option>
                    {getCategoryOptions().map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Image</label>
                  
                  {/* Image preview */}
                  {imagePreview && (
                    <div style={{
                      marginBottom: '12px',
                      padding: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      borderRadius: '12px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: '120px',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          display: 'block'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview('');
                          setFormData({ ...formData, imageUrl: '' });
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 12px',
                          backgroundColor: '#ff3b30',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}

                  {/* File upload */}
                  <div style={{ marginBottom: '12px' }}>
                    <label className="form-label" htmlFor="imageFile" style={{ fontSize: '0.8rem' }}>
                      Upload Image File (max 2MB)
                    </label>
                    <input
                      id="imageFile"
                      type="file"
                      accept="image/*"
                      className="form-input"
                      onChange={handleImageFileChange}
                      style={{ paddingTop: '8px', paddingBottom: '8px' }}
                    />
                  </div>

                  {/* URL input */}
                  <div>
                    <label className="form-label" htmlFor="imageUrl" style={{ fontSize: '0.8rem' }}>
                      Or paste image URL
                    </label>
                    <input
                      id="imageUrl"
                      type="url"
                      className="form-input"
                      value={formData.imageUrl}
                      onChange={(e) => handleImageUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#1d1d1f'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.available}
                      onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: '#0071e3'
                      }}
                    />
                    Available for ordering
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                  >
                    {editingItem ? 'Update Item' : 'Create Item'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Category Form */}
          {(isCreatingCategory || editingCategory) && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {editingCategory ? 'Edit Category' : 'Create New Category'}
                </h3>
                {editingCategory && (
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#0071e3',
                    fontWeight: '500',
                    marginTop: '4px'
                  }}>
                    Editing: {editingCategory.name}
                  </div>
                )}
              </div>

              <form onSubmit={handleCategorySubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="categoryName">Name *</label>
                  <input
                    id="categoryName"
                    type="text"
                    className="form-input"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    placeholder="Enter category name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="categoryDescription">Description</label>
                  <textarea
                    id="categoryDescription"
                    className="form-textarea"
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                    placeholder="Describe the category..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="categoryDisplayOrder">Display Order</label>
                  <input
                    id="categoryDisplayOrder"
                    type="number"
                    min="0"
                    className="form-input"
                    value={categoryFormData.displayOrder}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, displayOrder: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <small style={{ fontSize: '0.75rem', color: '#86868b' }}>
                    Lower numbers appear first in the menu
                  </small>
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#1d1d1f'
                  }}>
                    <input
                      type="checkbox"
                      checked={categoryFormData.isActive}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, isActive: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: '#0071e3'
                      }}
                    />
                    Active (visible in menu)
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                  >
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetCategoryForm}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Mobile Modal Forms */}
      {/* Menu Item Modal */}
      <Modal
        isOpen={isMobile && (isCreating || editingItem !== null)}
        title={editingItem ? 'Edit Menu Item' : 'Create New Menu Item'}
        onClose={() => {
          resetForm();
          setIsCreating(false);
          setEditingItem(null);
        }}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="mobileItemName">Name *</label>
            <input
              id="mobileItemName"
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter item name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="mobileItemDescription">Description</label>
            <textarea
              id="mobileItemDescription"
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the item..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="mobileItemPrice">Price *</label>
            <input
              id="mobileItemPrice"
              type="number"
              step="0.01"
              min="0"
              className="form-input"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="0.00"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="mobileItemCategory">Category</label>
            <select
              id="mobileItemCategory"
              className="form-select"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <option value="">Select a category</option>
              {getCategoryOptions().map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Image</label>
            
            {/* Image preview */}
            {imagePreview && (
              <div style={{
                marginBottom: '12px',
                padding: '12px',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: '12px',
                backgroundColor: '#f9f9f9'
              }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    display: 'block'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview('');
                    setFormData({ ...formData, imageUrl: '' });
                  }}
                  style={{
                    marginTop: '8px',
                    padding: '4px 12px',
                    backgroundColor: '#ff3b30',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                >
                  Remove Image
                </button>
              </div>
            )}

            {/* File upload */}
            <div style={{ marginBottom: '12px' }}>
              <label className="form-label" htmlFor="mobileImageFile" style={{ fontSize: '0.8rem' }}>
                Upload Image File (max 2MB)
              </label>
              <input
                id="mobileImageFile"
                type="file"
                accept="image/*"
                className="form-input"
                onChange={handleImageFileChange}
                style={{ paddingTop: '8px', paddingBottom: '8px' }}
              />
            </div>

            {/* URL input */}
            <div>
              <label className="form-label" htmlFor="mobileImageUrl" style={{ fontSize: '0.8rem' }}>
                Or paste image URL
              </label>
              <input
                id="mobileImageUrl"
                type="url"
                className="form-input"
                value={formData.imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#1d1d1f'
            }}>
              <input
                type="checkbox"
                checked={formData.available}
                onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#0071e3'
                }}
              />
              Available for ordering
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              {editingItem ? 'Update Item' : 'Create Item'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                resetForm();
                setIsCreating(false);
                setEditingItem(null);
              }}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal
        isOpen={isMobile && (isCreatingCategory || editingCategory !== null)}
        title={editingCategory ? 'Edit Category' : 'Create New Category'}
        onClose={() => {
          resetCategoryForm();
          setIsCreatingCategory(false);
          setEditingCategory(null);
        }}
      >
        <form onSubmit={handleCategorySubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="mobileCategoryName">Name *</label>
            <input
              id="mobileCategoryName"
              type="text"
              className="form-input"
              value={categoryFormData.name}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
              placeholder="Enter category name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="mobileCategoryDescription">Description</label>
            <textarea
              id="mobileCategoryDescription"
              className="form-textarea"
              value={categoryFormData.description}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
              placeholder="Describe the category..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="mobileCategoryOrder">Display Order</label>
            <input
              id="mobileCategoryOrder"
              type="number"
              min="0"
              className="form-input"
              value={categoryFormData.displayOrder}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, displayOrder: parseInt(e.target.value) || 0 })}
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#1d1d1f'
            }}>
              <input
                type="checkbox"
                checked={categoryFormData.isActive}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, isActive: e.target.checked })}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#0071e3'
                }}
              />
              Active category
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              {editingCategory ? 'Update Category' : 'Create Category'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                resetCategoryForm();
                setIsCreatingCategory(false);
                setEditingCategory(null);
              }}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
      </div>
    </div>
  );
};

export default MenuManagement; 